import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { priceService } from '../../services/priceService';
import './MainHeader.css';
import UserMenu from './UserMenu';
import DepositModal from '../modals/DepositModal';
import WithdrawalModal from '../modals/WithdrawalModal';
import ProfileImage from '../../assets/profile.png';

import './MainHeader.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const MainHeader: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isConnected, user } = useSelector((state: RootState) => state.auth);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState(false);
  const [isHamburgerMenuOpen, setIsHamburgerMenuOpen] = useState(false);
  const [solPrice, setSolPrice] = useState(186); // Default fallback price
  const [displayCurrency, setDisplayCurrency] = useState<'USD' | 'SOL'>('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const currencyDropdownTimer = React.useRef<NodeJS.Timeout | null>(null);
  const hamburgerMenuRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fetch SOL price on component mount
    const fetchPrice = async () => {
      try {
        const price = await priceService.getSOLPrice();
        setSolPrice(price);
      } catch (error) {
        console.warn('Failed to fetch SOL price:', error);
      }
    };

    // Load saved currency preference
    const savedCurrency = localStorage.getItem('balanceDisplayCurrency') as 'USD' | 'SOL';
    if (savedCurrency) {
      setDisplayCurrency(savedCurrency);
    }

    fetchPrice();

    // Update price every 5 minutes
    const interval = setInterval(fetchPrice, 5 * 60 * 1000);
    return () => {
      clearInterval(interval);
      const timer = currencyDropdownTimer.current;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  // Close hamburger menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hamburgerMenuRef.current && !hamburgerMenuRef.current.contains(event.target as Node)) {
        setIsHamburgerMenuOpen(false);
      }
    };

    if (isHamburgerMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHamburgerMenuOpen]);

  const handleAuthClick = (type: 'login' | 'register') => {
    navigate(`/${type}`);
  };

  const handleLogout = async () => {
    try {
      dispatch(logout());
      dispatch(addNotification({
        type: 'info',
        title: 'Logged Out',
        message: 'You have been logged out successfully',
      }));
      setIsHamburgerMenuOpen(false);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getProfileImageSrc = () => {
    if (user && user.profilePicture) {
      if (user.profilePicture.startsWith('http')) {
        return user.profilePicture;
      }
      return `${API_BASE_URL}/api/profile/picture/${user.profilePicture}`;
    }
    return ProfileImage;
  };

  const handleMouseEnter = () => {
    if (currencyDropdownTimer.current) {
      clearTimeout(currencyDropdownTimer.current);
    }
    setShowCurrencyDropdown(true);
  };

  const handleMouseLeave = () => {
    currencyDropdownTimer.current = setTimeout(() => {
      setShowCurrencyDropdown(false);
    }, 500); // 500ms delay before closing
  };

  const toggleCurrency = (currency: 'USD' | 'SOL') => {
    setDisplayCurrency(currency);
    localStorage.setItem('balanceDisplayCurrency', currency);
  };

  // Get balance display based on selected currency
  const getBalanceDisplay = () => {
    if (!user || !user.balance || isNaN(user.balance)) return '0.00';
    const solBalance = user.balance / 1_000_000_000; // Convert lamports to SOL
    
    if (displayCurrency === 'SOL') {
      return `${solBalance.toFixed(3)} SOL`;
    } else {
      const usdBalance = solBalance * solPrice; // Use live price
      return `$${usdBalance.toFixed(2)}`;
    }
  };

  return (
    <>
      <header className="main-header">
        <div className="header-left">
          <div className="logo">
            <img src="/logo.png" alt="SolBlox" className="logo-image" />
          </div>

          {/* Desktop: Coinflip button next to logo */}
          <button 
            className="cases-btn desktop-only"
            onClick={() => navigate('/coinflip')}
          >
            Coinflip
          </button>

          {/* Mobile: Coinflip button (matches desktop styling) */}
          <button 
            className="cases-btn mobile-only"
            onClick={() => navigate('/coinflip')}
          >
            Coinflip
          </button>
        </div>
        
        <div className="header-right">
          {/* Desktop: Original layout with Balance and UserMenu */}
          <div className="desktop-only desktop-header-right">
            {isConnected && user ? (
              <>
                <div className="balance-container-wrapper">
                  <div 
                    className="balance-container"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button className="balance-btn" onClick={() => setIsDepositModalOpen(true)}>
                      <div className="balance-left">
                        <svg className="wallet-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                        </svg>
                      </div>
                      <span className="balance-amount">{getBalanceDisplay()}</span>
                    </button>
                  
                    {showCurrencyDropdown && (
                      <div 
                        className="currency-dropdown"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        <button 
                          className={`currency-option ${displayCurrency === 'USD' ? 'active' : ''}`}
                          onClick={() => toggleCurrency('USD')}
                        >
                          USD ($)
                        </button>
                        <button 
                          className={`currency-option ${displayCurrency === 'SOL' ? 'active' : ''}`}
                          onClick={() => toggleCurrency('SOL')}
                        >
                          SOL
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <UserMenu />
              </>
            ) : (
              <div className="auth-buttons">
                <button 
                  className="login-btn"
                  onClick={() => handleAuthClick('login')}
                >
                  Login
                </button>
                <button 
                  className="signup-btn"
                  onClick={() => handleAuthClick('register')}
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>

          {/* Mobile: Balance and Hamburger Menu */}
          <div className="mobile-header-right mobile-only">
            {isConnected && user && (
              <div 
                className="balance-container mobile-balance"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button className="balance-btn" onClick={() => setIsDepositModalOpen(true)}>
                  <div className="balance-left">
                    <svg className="wallet-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                    </svg>
                  </div>
                  <span className="balance-amount">{getBalanceDisplay()}</span>
                </button>
              
                {showCurrencyDropdown && (
                  <div 
                    className="currency-dropdown"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button 
                      className={`currency-option ${displayCurrency === 'USD' ? 'active' : ''}`}
                      onClick={() => toggleCurrency('USD')}
                    >
                      USD ($)
                    </button>
                    <button 
                      className={`currency-option ${displayCurrency === 'SOL' ? 'active' : ''}`}
                      onClick={() => toggleCurrency('SOL')}
                    >
                      SOL
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <div className="hamburger-menu-container" ref={hamburgerMenuRef}>
              <button 
                className="hamburger-btn"
                onClick={() => setIsHamburgerMenuOpen(!isHamburgerMenuOpen)}
                aria-label="Menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>

              {/* Mobile Hamburger Dropdown */}
              {isHamburgerMenuOpen && (
                <div className="hamburger-dropdown">
                  {isConnected && user && (
                    <div className="hamburger-profile-section">
                      <img 
                        src={getProfileImageSrc()} 
                        alt="Profile" 
                        className="hamburger-profile-image"
                        onError={(e) => {
                          e.currentTarget.src = ProfileImage;
                        }}
                      />
                      <span className="hamburger-username">{user.username || 'User'}</span>
                    </div>
                  )}

                  {isConnected && user ? (
                    <>
                      <button 
                        className="hamburger-menu-item"
                        onClick={() => { navigate('/settings'); setIsHamburgerMenuOpen(false); }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path fillRule="evenodd" clipRule="evenodd" d="M18.51 9.49h1.87c.88 0 1.6.72 1.6 1.6v1.8c0 .88-.72 1.6-1.6 1.6h-1.87c-.03.092-.068.179-.106.269l-.034.081 1.32 1.32c.62.62.62 1.64 0 2.26l-1.27 1.27c-.62.62-1.64.62-2.26 0l-1.32-1.32c-.064.029-.131.055-.2.08l-.15.06v1.87c0 .88-.72 1.6-1.6 1.6h-1.8c-.88 0-1.6-.72-1.6-1.6v-1.87c-.092-.03-.179-.068-.269-.106l-.081-.034-1.32 1.32c-.62.62-1.64.62-2.26 0l-1.27-1.27c-.62-.62-.62-1.64 0-2.26l1.32-1.32c-.029-.064-.055-.131-.08-.2l-.06-.15H3.6c-.88 0-1.6-.72-1.6-1.6v-1.8c0-.88.72-1.6 1.6-1.6h1.87c.03-.092.068-.179.106-.269l.034-.081-1.32-1.32c-.62-.62-.62-1.64 0-2.26l1.27-1.27c.62-.62 1.64-.62 2.26 0l1.32 1.32c.064-.029.131-.055.2-.08l.15-.06V3.6c0-.88.72-1.6 1.6-1.6h1.8c.88 0 1.6.72 1.6 1.6v1.87c.092.03.179.068.269.106l.081.034 1.32-1.32c.62-.62 1.64-.62 2.26 0l1.27 1.27c.62.62.62 1.64 0 2.26l-1.32 1.32c.029.064.055.131.08.2l.06.15ZM16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" fill="#ffffff"></path>
                        </svg>
                        Settings
                      </button>
                      <button 
                        className="hamburger-menu-item"
                        onClick={() => { navigate('/settings?tab=transactions'); setIsHamburgerMenuOpen(false); }}
                      >
                        <svg width="18" height="18" fill="#ffffff" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"></path>
                        </svg>
                        Transactions
                      </button>
                      <button 
                        className="hamburger-menu-item"
                        onClick={() => { setIsWithdrawalModalOpen(true); setIsHamburgerMenuOpen(false); }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 19V5M5 12l7-7 7 7"/>
                        </svg>
                        Withdraw
                      </button>
                      {user.isAdmin && (
                        <button 
                          className="hamburger-menu-item"
                          onClick={() => { navigate('/moneymandavetj'); setIsHamburgerMenuOpen(false); }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                            <path d="M12 7v5l3 3"/>
                          </svg>
                          Admin
                        </button>
                      )}
                      <button 
                        className="hamburger-menu-item disconnect"
                        onClick={handleLogout}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                          <polyline points="16 17 21 12 16 7"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        className="hamburger-menu-item"
                        onClick={() => { handleAuthClick('login'); setIsHamburgerMenuOpen(false); }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                          <polyline points="10 17 15 12 10 7"></polyline>
                          <line x1="15" y1="12" x2="3" y2="12"></line>
                        </svg>
                        Login
                      </button>
                      <button 
                        className="hamburger-menu-item signup"
                        onClick={() => { handleAuthClick('register'); setIsHamburgerMenuOpen(false); }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="8.5" cy="7" r="4"></circle>
                          <line x1="20" y1="8" x2="20" y2="14"></line>
                          <line x1="23" y1="11" x2="17" y2="11"></line>
                        </svg>
                        Sign Up
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <DepositModal 
        isOpen={isDepositModalOpen} 
        onClose={() => setIsDepositModalOpen(false)} 
      />

      <WithdrawalModal 
        isOpen={isWithdrawalModalOpen} 
        onClose={() => setIsWithdrawalModalOpen(false)} 
      />
    </>
  );
};

export default MainHeader;